import React from 'react';

import dayjs from 'dayjs';
import 'dayjs/locale/bg';
import relativeTime from 'dayjs/plugin/relativeTime';
import { FormattedMessage, MessageDescriptor, useIntl } from 'react-intl';

import { MonitoringHealthIssue } from 'src/features/monitoring/types';
import { parkingStatusMessages } from 'src/features/monitoring/utils/messages';
import { ComponentStatusBadgeOptions } from 'src/features/systems/utils/constants';
import { systemsMessages } from 'src/features/systems/utils/messages';
import { Badge } from 'src/registry/ui/Badge';
import { Skeleton } from 'src/registry/ui/Skeleton';

dayjs.extend(relativeTime);

const ISSUE_SOURCE_MESSAGES: Record<string, MessageDescriptor> = {
    paystation_event: parkingStatusMessages.issueSourcePaystationEvent,
    heartbeat: parkingStatusMessages.issueSourceHeartbeat,
    periodic_check: parkingStatusMessages.issueSourcePeriodicCheck,
    self_report: parkingStatusMessages.issueSourceSelfReport,
    docker: parkingStatusMessages.issueSourceDocker,
    manual: parkingStatusMessages.issueSourceManual,
};

export default function MonitoringIssuesList({
    isLoading,
    issues,
    fallbackCount,
}: {
    isLoading: boolean;
    issues: MonitoringHealthIssue[] | undefined;
    fallbackCount: number;
}) {
    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-2 border-b border-gray-100 pb-2">
                <span className="text-sm font-semibold text-gray-900">
                    <FormattedMessage {...systemsMessages.activeIssues} />
                </span>
                <span className="text-xs font-medium text-gray-500">
                    {issues?.length ?? fallbackCount}
                </span>
            </div>
            {isLoading && !issues && (
                <div className="flex flex-col gap-2">
                    <Skeleton className="h-10" />
                    <Skeleton className="h-10" />
                </div>
            )}
            {issues && issues.length === 0 && (
                <span className="text-xs text-gray-500">
                    <FormattedMessage {...systemsMessages.noActiveIssues} />
                </span>
            )}
            {issues && issues.length > 0 && (
                <ul className="flex max-h-72 flex-col gap-2 overflow-y-auto">
                    {issues.map((issue) => (
                        <IssueItem key={issue.id} issue={issue} />
                    ))}
                </ul>
            )}
        </div>
    );
}

function IssueItem({ issue }: { issue: MonitoringHealthIssue }) {
    const { formatMessage, locale } = useIntl();
    const statusOption = ComponentStatusBadgeOptions[issue.severity];

    const startedAt = React.useMemo(() => dayjs(issue.started_at).locale(locale), [issue.started_at, locale]);
    const sourceMessage = ISSUE_SOURCE_MESSAGES[issue.source] ?? parkingStatusMessages.issueSourceUnknown;

    return (
        <li className="flex flex-col gap-1 rounded-md border border-gray-100 bg-gray-50/50 p-2">
            <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-xs text-gray-900 break-all">{issue.code}</span>
                <Badge theme={statusOption.theme} className="shrink-0 text-[10px]">
                    {formatMessage(statusOption.label)}
                </Badge>
            </div>
            <div className="flex items-center justify-between gap-2 text-[11px] text-gray-500">
                <span>{formatMessage(sourceMessage)}</span>
                <span title={startedAt.format('YYYY-MM-DD HH:mm:ss')}>
                    <FormattedMessage
                        {...parkingStatusMessages.issueStarted}
                        values={{ time: startedAt.fromNow() }}
                    />
                </span>
            </div>
        </li>
    );
}
